document.documentElement.dataset.wishlistScriptLoaded = 'true';

class BlushWishlist {
  constructor(root) {
    this.root = root;
    this.panel = root.querySelector('.wishlist-drawer__panel');
    this.list = root.querySelector('[data-wishlist-list]');
    this.empty = root.querySelector('[data-wishlist-empty]');
    this.status = root.querySelector('[data-wishlist-status]');
    this.liveRegion = document.querySelector('[data-wishlist-live-region]');
    this.toast = document.querySelector('[data-wishlist-toast]');
    this.toastMessage = this.toast?.querySelector('[data-wishlist-toast-message]');
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    this.storageKey = root.dataset.storageKey;
    this.rootUrl = root.dataset.rootUrl.endsWith('/') ? root.dataset.rootUrl : `${root.dataset.rootUrl}/`;
    this.locale = root.dataset.locale || document.documentElement.lang || 'uk';
    this.currency = root.dataset.currency || 'UAH';
    this.items = this.readItems();
    this.trigger = null;
    this.addedOverflowLock = false;
    this.refreshing = false;
    this.toastTimer = null;
    this.toastHideTimer = null;

    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleStorage = this.handleStorage.bind(this);

    this.mountProductToggle();
    this.updateUi();
    this.render();

    document.addEventListener('click', this.handleClick);
    document.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('storage', this.handleStorage);
    document.addEventListener('shopify:section:load', () => this.syncButtons(document));

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.matches?.('[data-wishlist-toggle]') || node.querySelector?.('[data-wishlist-toggle]')) {
            this.syncButtons(node);
          }
        });
      });
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  readItems() {
    try {
      const stored = JSON.parse(localStorage.getItem(this.storageKey) || '[]');
      if (!Array.isArray(stored)) return [];

      const seen = new Set();
      return stored
        .map((item) => this.normalizeItem(item))
        .filter((item) => {
          if (!item || seen.has(item.id)) return false;
          seen.add(item.id);
          return true;
        });
    } catch (error) {
      return [];
    }
  }

  normalizeItem(item) {
    if (!item) return null;

    const id = String(item.id || '').trim();
    const handle = String(item.handle || '').trim();
    if (!id || !handle) return null;

    return {
      id,
      variantId: String(item.variantId || '').trim(),
      handle,
      url: String(item.url || ''),
      title: String(item.title || ''),
      vendor: String(item.vendor || ''),
      image: String(item.image || ''),
      price: Number.isFinite(Number(item.price)) ? Number(item.price) : 0,
      available: item.available !== false,
      unavailable: item.unavailable === true,
    };
  }

  saveItems() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.items));
      return true;
    } catch (error) {
      this.announce(this.root.dataset.storageError);
      return false;
    }
  }

  mountProductToggle() {
    const template = document.querySelector('template[data-wishlist-product-template]');
    const title = document.querySelector('#MainContent .product__title');
    if (!template || !title || title.querySelector('[data-wishlist-toggle]')) return;

    title.classList.add('product__title--wishlist');
    title.append(template.content.cloneNode(true));
  }

  handleClick(event) {
    const toggle = event.target.closest('[data-wishlist-toggle]');
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      this.toggle(toggle);
      return;
    }

    const removeButton = event.target.closest('[data-wishlist-remove]');
    if (removeButton) {
      event.preventDefault();
      this.remove(removeButton.dataset.wishlistRemove);
      return;
    }

    const addToCartButton = event.target.closest('[data-wishlist-add-to-cart]');
    if (addToCartButton) {
      event.preventDefault();
      this.addToCart(addToCartButton.dataset.wishlistAddToCart, addToCartButton);
      return;
    }

    if (event.target.closest('[data-wishlist-clear]')) {
      event.preventDefault();
      this.clear();
      return;
    }

    const opener = event.target.closest('[data-wishlist-open]');
    if (opener) {
      event.preventDefault();
      this.open(opener);
      return;
    }

    if (event.target.closest('[data-wishlist-close]')) {
      event.preventDefault();
      this.close();
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.root.hasAttribute('open')) this.close();
  }

  handleStorage(event) {
    if (event.key !== this.storageKey) return;
    this.items = this.readItems();
    this.updateUi();
    this.render();
  }

  itemFromButton(button) {
    return this.normalizeItem({
      id: button.dataset.productId,
      variantId: button.dataset.productVariantId,
      handle: button.dataset.productHandle,
      url: button.dataset.productUrl,
      title: button.dataset.productTitle,
      vendor: button.dataset.productVendor,
      image: button.dataset.productImage,
      price: button.dataset.productPrice,
      available: button.dataset.productAvailable === 'true',
    });
  }

  toggle(button) {
    const item = this.itemFromButton(button);
    if (!item) return;

    const existingIndex = this.items.findIndex((saved) => saved.id === item.id);
    let added = false;
    let message = '';
    if (existingIndex >= 0) {
      const [removed] = this.items.splice(existingIndex, 1);
      message = this.message('removedMessage', removed.title);
    } else {
      this.items.unshift(item);
      added = true;
      message = this.message('addedMessage', item.title);
    }

    const saved = this.saveItems();
    this.updateUi();
    this.render();

    if (saved) {
      this.announce(message);
      this.showToast(added ? this.root.dataset.addedToast : this.root.dataset.removedToast);
      if (added) this.celebrate(button);
    }
  }

  remove(id) {
    const index = this.items.findIndex((item) => item.id === String(id));
    if (index < 0) return;

    const [removed] = this.items.splice(index, 1);
    const saved = this.saveItems();
    this.updateUi();
    this.render();
    if (saved) {
      this.announce(this.message('removedMessage', removed.title));
      this.showToast(this.root.dataset.removedToast);
    }
  }

  clear() {
    if (this.items.length === 0) return;

    const previousItems = this.items;
    this.items = [];
    if (!this.saveItems()) {
      this.items = previousItems;
      return;
    }

    this.updateUi();
    this.render();
    this.announce(this.root.dataset.clearedToast);
    this.showToast(this.root.dataset.clearedToast);
  }

  async addToCart(id, button) {
    const itemIndex = this.items.findIndex((item) => item.id === String(id));
    if (itemIndex < 0 || button.disabled) return;

    const defaultLabel = button.textContent;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = this.root.dataset.addingToCartLabel;

    try {
      let item = this.items[itemIndex];
      if (!item.variantId) {
        item = await this.fetchProduct(item);
        this.items[itemIndex] = item;
        this.saveItems();
      }

      if (!item.variantId || item.unavailable || !item.available) {
        throw new Error(this.root.dataset.cartError);
      }

      const formData = new FormData();
      formData.append('id', item.variantId);
      formData.append('quantity', '1');

      if (this.cart?.getSectionsToRender) {
        const sections = this.cart.getSectionsToRender().map((section) => section.id);
        formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      const config = typeof fetchConfig === 'function'
        ? fetchConfig('javascript')
        : { method: 'POST', headers: { Accept: 'application/json' } };
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      const response = await fetch(window.routes?.cart_add_url || `${this.rootUrl}cart/add.js`, config);
      const cartState = await response.json();
      if (!response.ok || cartState.status) throw new Error(cartState.description || this.root.dataset.cartError);

      let cartUpdatePromise = Promise.resolve();
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        cartUpdatePromise = Promise.resolve(
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'wishlist',
            productVariantId: item.variantId,
            cartData: cartState,
          })
        ).catch(() => {});
      }

      this.announce(this.message('cartAddedMessage', item.title));
      this.showToast(this.root.dataset.cartAddedToast);

      if (this.cart?.renderContents) {
        const returnFocus = this.trigger || document.querySelector('[data-wishlist-open]');
        await cartUpdatePromise;
        this.close();
        this.cart.setActiveElement?.(returnFocus);
        this.cart.renderContents(cartState);
      } else if (window.routes?.cart_url) {
        window.location.assign(window.routes.cart_url);
      }
    } catch (error) {
      const message = this.root.dataset.cartError;
      this.announce(message);
      this.showToast(message);
    } finally {
      if (button.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.textContent = defaultLabel;
      }
    }
  }

  message(key, productTitle) {
    return (this.root.dataset[key] || '').replace('[product]', productTitle || '');
  }

  announce(message) {
    if (!message || !this.liveRegion) return;
    this.liveRegion.textContent = '';
    window.setTimeout(() => {
      this.liveRegion.textContent = message;
    }, 20);
  }

  showToast(message) {
    if (!message || !this.toast || !this.toastMessage) return;

    window.clearTimeout(this.toastTimer);
    window.clearTimeout(this.toastHideTimer);
    this.toastMessage.textContent = message;
    this.toast.hidden = false;
    this.toast.classList.remove('is-visible');

    window.requestAnimationFrame(() => this.toast.classList.add('is-visible'));
    this.toastTimer = window.setTimeout(() => {
      this.toast.classList.remove('is-visible');
      this.toastHideTimer = window.setTimeout(() => {
        this.toast.hidden = true;
      }, 180);
    }, 2200);
  }

  celebrate(button) {
    button.classList.remove('is-celebrating');
    void button.offsetWidth;
    button.classList.add('is-celebrating');
    window.setTimeout(() => button.classList.remove('is-celebrating'), 440);
  }

  updateUi() {
    const count = this.items.length;

    document.querySelectorAll('[data-wishlist-count]').forEach((badge) => {
      badge.textContent = count > 99 ? '99+' : String(count);
      badge.hidden = count === 0;
    });

    document.querySelectorAll('[data-wishlist-open]').forEach((button) => {
      const label = button.dataset.openLabel || button.getAttribute('aria-label') || '';
      button.setAttribute('aria-label', count ? `${label} (${count})` : label);
      button.classList.toggle('is-active', count > 0);
    });

    this.root.querySelectorAll('[data-wishlist-drawer-count]').forEach((badge) => {
      badge.textContent = String(count);
    });

    this.root.querySelectorAll('[data-wishlist-clear]').forEach((button) => {
      button.hidden = count === 0;
      button.disabled = count === 0;
    });

    this.syncButtons(document);
  }

  syncButtons(scope) {
    const buttons = [];
    if (scope.matches?.('[data-wishlist-toggle]')) buttons.push(scope);
    scope.querySelectorAll?.('[data-wishlist-toggle]').forEach((button) => buttons.push(button));

    const savedIds = new Set(this.items.map((item) => item.id));
    buttons.forEach((button) => {
      const active = savedIds.has(String(button.dataset.productId));
      const label = active ? button.dataset.removeLabel : button.dataset.addLabel;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', label || '');
      button.setAttribute('title', label || '');
    });
  }

  open(trigger) {
    if (this.root.hasAttribute('open')) return;

    this.trigger = trigger || document.activeElement;
    this.addedOverflowLock = !document.body.classList.contains('overflow-hidden');
    document.body.classList.add('overflow-hidden');
    this.root.setAttribute('open', '');
    this.root.setAttribute('aria-hidden', 'false');
    this.render();

    window.requestAnimationFrame(() => {
      if (typeof trapFocus === 'function') {
        trapFocus(this.panel);
      } else {
        this.panel.focus();
      }
    });

    this.refreshProducts();
  }

  close() {
    if (!this.root.hasAttribute('open')) return;

    this.root.removeAttribute('open');
    this.root.setAttribute('aria-hidden', 'true');
    if (this.addedOverflowLock) document.body.classList.remove('overflow-hidden');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.trigger);
    } else {
      this.trigger?.focus();
    }
  }

  render() {
    const hasItems = this.items.length > 0;
    this.empty.hidden = hasItems;
    this.list.hidden = !hasItems;
    this.list.replaceChildren(...this.items.map((item) => this.createItem(item)));
  }

  createItem(item) {
    const productUrl = this.productUrl(item);
    const row = document.createElement('li');
    row.className = 'wishlist-item';
    row.dataset.productId = item.id;

    const imageLink = document.createElement('a');
    imageLink.className = 'wishlist-item__image-link';
    imageLink.href = productUrl;

    if (this.safeImageUrl(item.image)) {
      const image = document.createElement('img');
      image.className = 'wishlist-item__image';
      image.src = this.safeImageUrl(item.image);
      image.alt = item.title;
      image.loading = 'lazy';
      image.width = 200;
      image.height = 200;
      imageLink.append(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'wishlist-item__image-placeholder';
      placeholder.innerHTML = '<svg class="wishlist-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z"></path></svg>';
      imageLink.append(placeholder);
    }

    const details = document.createElement('div');
    details.className = 'wishlist-item__details';

    if (item.vendor) {
      const vendor = document.createElement('p');
      vendor.className = 'wishlist-item__vendor';
      vendor.textContent = item.vendor;
      details.append(vendor);
    }

    const title = document.createElement('a');
    title.className = 'wishlist-item__title';
    title.href = productUrl;
    title.textContent = item.title || item.handle;
    details.append(title);

    const price = document.createElement('p');
    price.className = 'wishlist-item__price';
    price.textContent = this.formatPrice(item.price);
    details.append(price);

    if (item.unavailable || !item.available) {
      const availability = document.createElement('p');
      availability.className = `wishlist-item__availability${item.unavailable ? ' wishlist-item__availability--unavailable' : ''}`;
      availability.textContent = item.unavailable ? this.root.dataset.unavailableLabel : this.root.dataset.soldOutLabel;
      details.append(availability);
    }

    const actions = document.createElement('div');
    actions.className = 'wishlist-item__actions';

    const addToCart = document.createElement('button');
    addToCart.type = 'button';
    addToCart.className = 'wishlist-item__add button button--secondary';
    addToCart.dataset.wishlistAddToCart = item.id;
    addToCart.disabled = item.unavailable || !item.available;
    addToCart.textContent = addToCart.disabled
      ? (item.unavailable ? this.root.dataset.unavailableLabel : this.root.dataset.soldOutLabel)
      : this.root.dataset.addToCartLabel;

    const secondaryActions = document.createElement('div');
    secondaryActions.className = 'wishlist-item__secondary-actions';

    const view = document.createElement('a');
    view.className = 'wishlist-item__view link';
    view.href = productUrl;
    view.textContent = this.root.dataset.viewLabel;

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'wishlist-item__remove';
    remove.dataset.wishlistRemove = item.id;
    remove.textContent = this.root.dataset.removeLabel;

    secondaryActions.append(view, remove);
    actions.append(addToCart, secondaryActions);
    details.append(actions);
    row.append(imageLink, details);
    return row;
  }

  productUrl(item) {
    const fallback = `${this.rootUrl}products/${encodeURIComponent(item.handle)}`;
    if (!item.url) return fallback;

    try {
      const url = new URL(item.url, window.location.origin);
      if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) return fallback;
      return `${url.pathname}${url.search}`;
    } catch (error) {
      return fallback;
    }
  }

  safeImageUrl(value) {
    if (!value) return '';
    if (value.startsWith('//')) return `https:${value}`;

    try {
      const url = new URL(value, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
    } catch (error) {
      return '';
    }
  }

  formatPrice(cents) {
    const amount = Number(cents) / 100;
    try {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (error) {
      return `${amount.toFixed(2)} ${this.currency}`;
    }
  }

  async refreshProducts() {
    if (this.refreshing || this.items.length === 0) return;
    this.refreshing = true;
    this.status.textContent = this.root.dataset.loadingLabel;
    this.status.hidden = false;
    this.list.setAttribute('aria-busy', 'true');

    await Promise.all(
      this.items.map(async (item) => {
        const updated = await this.fetchProduct(item);
        const index = this.items.findIndex((current) => current.id === item.id);
        if (index >= 0) this.items[index] = updated;
      })
    );

    this.saveItems();
    this.updateUi();
    this.render();
    this.status.hidden = true;
    this.list.removeAttribute('aria-busy');
    this.refreshing = false;
  }

  async fetchProduct(item) {
    try {
      const response = await fetch(`${this.rootUrl}products/${encodeURIComponent(item.handle)}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (response.status === 404) return { ...item, available: false, unavailable: true };
      if (!response.ok) return item;

      const product = await response.json();
      const featuredImage =
        typeof product.featured_image === 'string'
          ? product.featured_image
          : product.featured_image?.src || product.images?.[0] || item.image;
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const preferredVariant =
        variants.find((variant) => String(variant.id) === item.variantId && variant.available) ||
        variants.find((variant) => variant.available) ||
        variants[0];

      return {
        ...item,
        id: String(product.id || item.id),
        variantId: String(preferredVariant?.id || item.variantId || ''),
        handle: product.handle || item.handle,
        url: `${this.rootUrl}products/${encodeURIComponent(product.handle || item.handle)}`,
        title: product.title || item.title,
        vendor: product.vendor || item.vendor,
        image: featuredImage || item.image,
        price: Number.isFinite(Number(preferredVariant?.price ?? product.price))
          ? Number(preferredVariant?.price ?? product.price)
          : item.price,
        available: preferredVariant ? preferredVariant.available !== false : product.available !== false,
        unavailable: false,
      };
    } catch (error) {
      return item;
    }
  }
}

const initializeBlushWishlist = () => {
  const drawer = document.getElementById('WishlistDrawer');
  if (!drawer) return;

  try {
    window.blushWishlist = new BlushWishlist(drawer);
    drawer.dataset.wishlistReady = 'true';
  } catch (error) {
    drawer.dataset.wishlistReady = 'false';
    console.error('Blush favorites failed to initialize', error);
  }
};

if (document.readyState === 'loading' && !document.getElementById('WishlistDrawer')) {
  document.addEventListener('DOMContentLoaded', initializeBlushWishlist, { once: true });
} else {
  initializeBlushWishlist();
}
